import React, { useEffect } from 'react';
import { useProperty } from '../../context/PropertyContext';
import '../../styles/PropertySwitcher.css';

/**
 * PropertySwitcher Component - Owner Property Selection
 *
 * Global property switcher for property owners (turnKeyOwner)
 * - Uses PropertyContext for state management (persists across pages)
 * - Selection persists on page refresh (SessionStorage)
 * - Always visible (even for single property - shows property name)
 *
 * Pattern: Mirrors AccountSwitcher for consistency
 * Location: /components/common/ - Reusable across owner pages
 *
 * Usage: <PropertySwitcher properties={ownerProperties} />
 */
// Property code (P-000x) prefixes the name so ops can match a unit to invoices/reports
// without opening it. Native <option> can't hold markup, so it's plain text.
const propertyLabel = (property) =>
  property?.propertyCode ? `${property.propertyCode} — ${property.name}` : property?.name;

const PropertySwitcher = ({ properties, isLoading = false }) => {
  // Global state from Context
  const { selectedProperty, setSelectedProperty } = useProperty();

  // CRITICAL: Validate selectedProperty BEFORE rendering logic
  // This must run even when component will return null (no properties case)
  // Otherwise stale selections persist when admin switches to account with 0 properties
  useEffect(() => {
    if (selectedProperty && properties) {
      // Case 1: No properties at all - clear selection
      if (properties.length === 0) {
        setSelectedProperty(null);
        return;
      }

      // Case 2: Properties exist but selected one is not in the list
      const exists = properties.some(p => String(p._id) === String(selectedProperty));
      if (!exists) {
        setSelectedProperty(null);
      }
    }
  }, [properties, selectedProperty, setSelectedProperty]);


  // Auto-select if only one property
  useEffect(() => {
    if (properties && properties.length > 0 && !selectedProperty) {
      // No "all properties" option — pick the first available property by default
      setSelectedProperty(properties[0]._id);
    }
  }, [properties, selectedProperty, setSelectedProperty]);

  const handlePropertyChange = (propertyId) => {
    setSelectedProperty(propertyId);
  };

  if (isLoading) {
    return (
      <div className="property-switcher" dir="rtl">
        <label>
          <i className="bi bi-buildings"></i>
          العقار
        </label>
        <select className="property-select" disabled>
          <option>جاري التحميل...</option>
        </select>
      </div>
    );
  }

  if (!properties || properties.length === 0) {
    return null;
  }

  // Single property - show name (non-interactive or disabled)
  if (properties.length === 1) {
    return (
      <div className="property-switcher" dir="rtl">
        <label>
          <i className="bi bi-buildings"></i>
          العقار
        </label>
        <select className="property-select" disabled>
          <option>{propertyLabel(properties[0])}</option>
        </select>
      </div>
    );
  }

  // Multiple properties - interactive dropdown
  return (
    <div className="property-switcher" dir="rtl">
      <label>
        <i className="bi bi-buildings"></i>
        العقار
      </label>
      <select
        value={selectedProperty || properties[0]._id}
        onChange={(e) => handlePropertyChange(e.target.value)}
        className="property-select"
      >
        {properties.map(property => (
          <option key={property._id} value={property._id}>
            {propertyLabel(property)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default PropertySwitcher;
